import GiftCardTemplate from '@/assets/images/gift-card-template.webp';

/**
 * Overlay positions derived from pixel analysis of the client's original JPG
 * (Gift Card_Final01.jpg, 1801×1201px — the same image used in the admin portal).
 *
 *   VALUE box     : left 14.27 %, width 13.88 %, top 70.5 %, height 4.2 %
 *   SESSIONS box  : left 31.65 %, width 13.94 %, top 70.5 %, height 4.2 %
 *   CARD NUMBER   : left 14.27 %, width 31.32 %, top 82.5 %, height 5.1 %
 */

interface GiftCardVisualProps {
  /** Face-value price string e.g. "$99.99" */
  price?: string;
  /** Session count from the package */
  sessionCount?: number;
  /** Actual gift card code — shows placeholder dots when absent */
  cardNumber?: string;
}

export default function GiftCardVisual({ price, sessionCount, cardNumber }: GiftCardVisualProps) {
  const hasData = !!(price);

  const formattedCode = cardNumber
    ? cardNumber.replace(/(.{4})/g, '$1 ').trim()
    : '● ● ● ●   ● ● ● ●   ● ● ● ●   ● ● ● ●';

  return (
    // 3:2 aspect ratio matches the 6×4 inch template
    // containerType enables cqw font scaling relative to this element's width
    <div
      className='relative w-full select-none'
      style={{ aspectRatio: '3 / 2', containerType: 'inline-size' }}
    >
      {/* Background template */}
      <img
        src={GiftCardTemplate}
        alt='Gift card'
        draggable={false}
        className='absolute inset-0 w-full h-full object-cover rounded-xl'
      />

      {/* ── VALUE overlay ───────────────────────────────────────────────── */}
      <div
        className='absolute flex items-center justify-center'
        style={{
          left:   '14.27%',
          width:  '13.88%',
          top:    '70.5%',
          height: '4.2%',
        }}
      >
        <span
          className='font-bold text-center leading-none'
          style={{
            fontSize: 'clamp(8px, 1.6cqw, 22px)',
            color: hasData ? '#1a1a1a' : '#9CA3AF',
          }}
        >
          {hasData ? price : '—'}
        </span>
      </div>

      {/* ── SESSIONS overlay ────────────────────────────────────────────── */}
      <div
        className='absolute flex items-center justify-center'
        style={{
          left:   '31.65%',
          width:  '13.94%',
          top:    '70.5%',
          height: '4.2%',
        }}
      >
        <span
          className='font-bold text-center leading-none'
          style={{
            fontSize: 'clamp(8px, 1.6cqw, 22px)',
            color: hasData && sessionCount != null ? '#1a1a1a' : '#9CA3AF',
          }}
        >
          {hasData && sessionCount != null ? sessionCount : '—'}
        </span>
      </div>

      {/* ── GIFT CARD NUMBER overlay ─────────────────────────────────────── */}
      <div
        className='absolute flex items-center justify-center'
        style={{
          left:   '14.27%',
          width:  '31.32%',
          top:    '82.5%',
          height: '5.1%',
        }}
      >
        <span
          className='font-mono text-center tracking-widest leading-none'
          style={{
            fontSize: 'clamp(6px, 1.1cqw, 14px)',
            color: cardNumber ? '#1a1a1a' : '#9CA3AF',
            letterSpacing: '0.18em',
          }}
        >
          {formattedCode}
        </span>
      </div>
    </div>
  );
}
